import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Alert, Spinner } from 'react-bootstrap';
import { FaLink, FaArrowLeft } from 'react-icons/fa';
import { 
  getWebhookConnectionByCustomerId, 
  upsertWebhookConnection, 
  deleteWebhookConnection,
  parseApiResponse 
} from './webhookService';
import WebhookList from './WebhookList';
import WebhookForm from './WebhookForm';

export default function GuestyIntegration() {
  const { cosCustomerId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [webhookData, setWebhookData] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    cos_customer_id: cosCustomerId
  });

  // Fetch webhook data on component mount
  useEffect(() => {
    fetchWebhookData();
  }, [cosCustomerId]);

  const fetchWebhookData = () => {
    setLoading(true);
    setError(null);
    
    getWebhookConnectionByCustomerId(cosCustomerId)
      .then(async (response) => {
        const result = await parseApiResponse(response);
        
        if (result.success) {
          if (result.data.success && result.data.webhooks && result.data.webhooks.length > 0) {
            // Store all webhooks in state
            setWebhookData(result.data.webhooks);
            
            // Reset form for new integration
            setFormData({
              client_id: '',
              client_secret: '',
              cos_customer_id: cosCustomerId
            });
          } else {
            // No webhooks found
            setWebhookData([]);
          }
        } else {
          setError(result.error);
        }
      })
      .catch(error => {
        console.log('error', error);
        setError("An error occurred while fetching webhook data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    upsertWebhookConnection(formData)
      .then(async (response) => {
        const result = await parseApiResponse(response);
        
        if (result.success) {
          if (result.data.success) {
            setSuccess("Webhook connection created/updated successfully");
            fetchWebhookData(); // Refresh data
            
            // Clear the client secret field after successful save
            setFormData(prev => ({
              ...prev,
              client_secret: ''
            }));
          } else {
            setError(result.data.message || "Failed to create/update webhook connection");
          }
        } else {
          setError(result.error);
        }
      })
      .catch(error => {
        console.log('error', error);
        setError("An error occurred while creating/updating webhook connection");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleDelete = (webhookId) => {
    if (window.confirm("Are you sure you want to delete this webhook connection? This action cannot be undone.")) {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      
      deleteWebhookConnection(cosCustomerId, webhookId)
        .then(async (response) => {
          const result = await parseApiResponse(response);
          
          if (result.success) {
            if (result.data.success) {
              setSuccess("Webhook connection deleted successfully");
              
              // Refresh webhook data
              fetchWebhookData();
            } else {
              setError(result.data.message || "Failed to delete webhook connection");
            }
          } else {
            setError(result.error);
          }
        })
        .catch(error => {
          console.log('error', error);
          setError("An error occurred while deleting webhook connection");
        })
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Header as="h5" className="bg-primary text-white d-flex justify-content-between align-items-center">
          <div>
            <FaLink className="me-2" /> Guesty Integration for Customer ID: {cosCustomerId}
          </div>
          <button 
            className="btn btn-light btn-sm"
            onClick={() => navigate('/manage-user')}
          >
            <FaArrowLeft className="me-2" /> Back to Users
          </button>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="mb-4">
              {success}
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading webhook data...</p>
            </div>
          ) : (
            <>
              <WebhookList 
                webhooks={webhookData} 
                onDelete={handleDelete} 
                isDeleting={deleting} 
              />
              
              <WebhookForm 
                formData={formData}
                onChange={handleInputChange}
                onSubmit={handleSubmit}
                isSaving={saving}
                cosCustomerId={cosCustomerId}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
